import { Link as RouterLink } from 'react-router-dom';
import { DetailTagsValue } from '@/features/shared/components/entityDetails/DetailTagsValue';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import {
  resolveProjectDisplayStatus,
  resolveProjectDisplayStatusLabel,
} from '@/features/shared/utils/projectStatusPresentation';
import { getDetailLabel } from '@/shared/copy/details';
import { getStatusCopy } from '@/shared/copy/status';
import { s } from '@/theme/tokens';
import { detailInfoPanelSurfaceSx } from '@/ui/composites/detailInfoPanelSx';
import { InfoField } from '@/ui/composites/InfoField';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { LayoutGrid, LayoutGridItem } from '@/ui/layout/LayoutGrid';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Chip, Link, resolveStatusChipTone, Surface, Text } from '@/ui/primitives';
import type { MetricValueItem } from '@/features/metrics/components/metricValueItems';
import type { SuiteDetailDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

export type SuiteDetailsPanelsProps = {
  suite: SuiteDetailDto;
  metricValueItems: MetricValueItem[];
};

const findMetricValueItem = (
  metricValueItems: MetricValueItem[],
  id: 'passRate' | 'frequency' | 'avgDuration' | 'flakeScore',
): MetricValueItem | null => metricValueItems.find((item) => item.id === id) ?? null;

const renderMetricValue = (
  metricValueItem: MetricValueItem | null,
  noDataLabel: string,
): ReactElement | string =>
  metricValueItem ? (
    <Chip label={metricValueItem.valueLabel} tone={metricValueItem.valueTone} size="xs" />
  ) : (
    noDataLabel
  );

export const SuiteDetailsPanels = ({
  suite,
  metricValueItems,
}: SuiteDetailsPanelsProps): ReactElement => {
  const { groupErroredWithFailed } = useProjectStatusPresentation();
  const noDataLabel = getStatusCopy('noData');
  const passRateMetric = findMetricValueItem(metricValueItems, 'passRate');
  const frequencyMetric = findMetricValueItem(metricValueItems, 'frequency');
  const flakeScoreMetric = findMetricValueItem(metricValueItems, 'flakeScore');
  const avgDurationMetric = findMetricValueItem(metricValueItems, 'avgDuration');
  const projectLabel = suite.project?.name ?? suite.project?.slug ?? suite.projectId;
  const latestRun = suite.latestRun ?? null;
  const latestRunLabel =
    latestRun?.runNumber !== null && latestRun?.runNumber !== undefined
      ? `#${latestRun.runNumber}`
      : noDataLabel;
  const latestRunHref =
    suite.slug && latestRun?.runNumber ? `/suites/${suite.slug}/runs/${latestRun.runNumber}` : null;
  const latestTimestampLabel = latestRun
    ? formatDate(latestRun.timestamp, { includeTime: true }) || noDataLabel
    : noDataLabel;
  const latestDurationLabel =
    latestRun?.durationMs !== null && latestRun?.durationMs !== undefined
      ? formatDuration(latestRun.durationMs)
      : noDataLabel;
  const latestDisplayStatus = resolveProjectDisplayStatus(
    latestRun?.status ?? null,
    groupErroredWithFailed,
  );
  const latestStatusLabel = latestRun?.status
    ? resolveProjectDisplayStatusLabel(latestRun.status, groupErroredWithFailed)
    : noDataLabel;
  const latestStatusValue = latestRun?.status ? (
    <Chip
      label={latestStatusLabel}
      tone={resolveStatusChipTone(latestDisplayStatus)}
      size="xs"
      transform="uppercase"
    />
  ) : (
    latestStatusLabel
  );

  return (
    <Section paddingY="none">
      <Surface padding="lg" sx={detailInfoPanelSurfaceSx}>
        <LayoutGrid gap="md">
          <LayoutGridItem
            xs={12}
            sm={4}
            sx={{ borderRight: 1, borderColor: 'divider', display: 'flex' }}
          >
            <Stack gap="sm" sx={{ paddingRight: s('md'), width: '100%' }}>
              <InfoField
                label={getDetailLabel('suite')}
                value={
                  <Text role="body" component="span">
                    {suite.name}
                  </Text>
                }
                gap="none"
              />
              <InfoField label={getDetailLabel('project')} value={projectLabel} gap="none" />
              <InfoField
                label={getDetailLabel('tags')}
                value={<DetailTagsValue tags={suite.tags} projectId={suite.projectId} />}
                gap="none"
              />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem
            xs={12}
            sm={4}
            sx={{ borderRight: 1, borderColor: 'divider', display: 'flex' }}
          >
            <Stack gap="sm" sx={{ paddingX: s('md'), width: '100%' }}>
              <InfoField
                label={getDetailLabel('passRate')}
                value={renderMetricValue(passRateMetric, noDataLabel)}
                gap="none"
              />
              <InfoField
                label={getDetailLabel('frequency')}
                value={renderMetricValue(frequencyMetric, noDataLabel)}
                gap="none"
              />
              <InfoField
                label={getDetailLabel('avgDuration')}
                value={renderMetricValue(avgDurationMetric, noDataLabel)}
                gap="none"
              />
              <InfoField
                label={getDetailLabel('flakeScore')}
                value={renderMetricValue(flakeScoreMetric, noDataLabel)}
                gap="none"
              />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem xs={12} sm={4} sx={{ display: 'flex' }}>
            <Stack gap="sm" sx={{ paddingLeft: s('md'), width: '100%' }}>
              <InfoField
                label={getDetailLabel('latestRun')}
                value={
                  latestRunHref ? (
                    <Link
                      component={RouterLink}
                      to={latestRunHref}
                      underline="hover"
                      color="inherit"
                    >
                      {latestRunLabel}
                    </Link>
                  ) : (
                    latestRunLabel
                  )
                }
                gap="none"
              />
              <InfoField
                label={getDetailLabel('latestStatus')}
                value={latestStatusValue}
                gap="none"
              />
              <InfoField
                label={getDetailLabel('latestTimestamp')}
                value={latestTimestampLabel}
                gap="none"
              />
              <InfoField
                label={getDetailLabel('latestDuration')}
                value={latestDurationLabel}
                gap="none"
              />
            </Stack>
          </LayoutGridItem>
        </LayoutGrid>
      </Surface>
    </Section>
  );
};
